/**
 * CardcomConfigManager — the list of Cardcom webhook configurations.
 *
 * Each row: what it matches (a payment page by Custom19, or an item by SKU), what it does on a
 * successful payment (tags, status for NEW customers, Schooler course, tracking event) and
 * whether it's active. Search filters by number / description (client-side).
 *
 * Data (unchanged): getCardcomConfigs · deleteCardcomConfig (after withConfirmAlert).
 * getSchoolerCourses only gives the course column its Hebrew names (key → name).
 * New / edit opens CardcomConfigUpdater (drawer).
 */
import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';

import * as actionsCreators from './../../../actions/index.js';
import {
  DataTable,
  RowActions,
  EmptyState,
  Button,
  Input,
  StatusBadge,
  TagChips,
  Tooltip,
  statusColor,
  useBreakpoint,
} from '../../../ui';
import CardcomConfigUpdater from './CardcomConfigUpdater';
import classes from './CardcomConfigManager.css';

const CardcomConfigManager = (props) => {
  const [configs, setConfigs] = useState([]);
  const [showUpdater, setShowUpdater] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [updaterFlag, setUpdaterFlag] = useState('add');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); // UI only
  const [courseNames, setCourseNames] = useState({}); // { key: Hebrew name } — display only
  const screens = useBreakpoint();

  useEffect(() => {
    loadConfigs();
    props
      .getSchoolerCourses()
      .then((courses) => setCourseNames(Object.fromEntries(courses.map((c) => [c.key, c.name]))))
      .catch(() => {}); // no names → the key is shown, as before
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const results = await props.getCardcomConfigs();
      setConfigs(results);
    } catch (error) {
      console.error('Error loading configs:', error);
      props.showAlert('שגיאה בטעינת תצורות Cardcom');
    }
    setLoading(false);
  };

  const onOpenUpdater = (flag, config = null) => {
    setUpdaterFlag(flag);
    setSelectedConfig(config);
    setShowUpdater(true);
  };

  const onCloseUpdater = (shouldRefresh) => {
    setShowUpdater(false);
    setSelectedConfig(null);
    if (shouldRefresh) loadConfigs();
  };

  const onDeleteConfig = async (config) => {
    props.withConfirmAlert(async () => {
      try {
        await props.deleteCardcomConfig(config.id);
        props.showAlert('התצורה נמחקה בהצלחה');
        loadConfigs();
      } catch (error) {
        console.error('Error deleting config:', error);
        props.showAlert('שגיאה במחיקת התצורה');
      }
    }, `למחוק את התצורה ${config.numberId}${config.description ? ` (${config.description})` : ''}?`);
  };

  const q = search.trim().toLowerCase();
  const visible = q
    ? configs.filter((c) => `${c.numberId} ${c.description || ''}`.toLowerCase().includes(q))
    : configs;

  const dash = <span className={classes.muted}>—</span>;

  return (
    <div className={classes.tab}>
      {showUpdater && <CardcomConfigUpdater config={selectedConfig} flag={updaterFlag} onClose={onCloseUpdater} />}

      <div className={classes.toolbar}>
        <Input allowClear prefix={<SearchOutlined />} placeholder="חיפוש לפי מספר או תיאור…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => onOpenUpdater('add')}>
          תצורה חדשה
        </Button>
      </div>

      <DataTable
        data={visible}
        loading={loading}
        fit
        onRowClick={(c) => onOpenUpdater('update', c)}
        empty={
          configs.length === 0 ? (
            <EmptyState
              title="אין עדיין תצורות"
              description="תצורה קובעת מה קורה ב-CRM כשמישהו משלם בעמוד או קונה פריט ב-Cardcom"
              action={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => onOpenUpdater('add')}>
                  תצורה חדשה
                </Button>
              }
            />
          ) : (
            <EmptyState title="לא נמצאו תצורות" />
          )
        }
        columns={[
          {
            key: 'numberId',
            title: 'עמוד / פריט',
            render: (_, c) => (
              <div className={classes.matchCell}>
                <span className={classes.numberLine}>
                  <Tooltip title={c.matchType === 'item' ? 'פריט בעסקה (מק"ט)' : 'עמוד תשלום (Custom19)'}>
                    <span className={classes.matchType} data-type={c.matchType === 'item' ? 'item' : 'page'}>
                      {c.matchType === 'item' ? 'פריט' : 'עמוד'}
                    </span>
                  </Tooltip>
                  <span className={classes.number} dir="ltr">
                    {c.numberId}
                  </span>
                </span>
                {c.description ? <span className={classes.description}>{c.description}</span> : null}
              </div>
            ),
          },
          {
            key: 'tags',
            title: 'טאגים',
            responsive: ['md'],
            render: (_, c) => <TagChips tags={c.tags || []} max={3} />,
          },
          {
            key: 'status',
            title: 'סטטוס ללקוח חדש',
            width: 170, // fits the title on one line; the course column gives up the space
            responsive: ['lg'],
            render: (_, c) => (c.status ? <StatusBadge color={statusColor(c.statusId)}>{c.status.name}</StatusBadge> : dash),
          },
          {
            key: 'course',
            title: 'קורס',
            responsive: ['lg'],
            render: (_, c) =>
              c.schoolerCourse ? (
                // Hebrew name; the stored key in the tooltip (unknown key → the key itself)
                <Tooltip title={<span dir="ltr">{c.schoolerCourse}</span>}>
                  <span className={classes.course}>{courseNames[c.schoolerCourse] || c.schoolerCourse}</span>
                </Tooltip>
              ) : (
                dash
              ),
          },
          {
            key: 'tracking',
            title: 'אירוע מעקב',
            width: 130,
            responsive: ['xl'],
            render: (_, c) => (c.trackingEventType ? <code className={classes.code}>{c.trackingEventType}</code> : dash),
          },
          {
            key: 'isActive',
            title: 'מצב',
            width: 90,
            responsive: ['sm'],
            render: (_, c) => <StatusBadge color={c.isActive ? '#16A34A' : '#A1A1AA'}>{c.isActive ? 'פעיל' : 'כבוי'}</StatusBadge>,
          },
          {
            key: 'actions',
            title: '',
            align: 'left',
            width: screens.md === false ? 64 : 90, // phones: a single "⋯" menu
            render: (_, c) => (
              <RowActions
                actions={[
                  { key: 'edit', icon: <EditOutlined />, label: 'עריכה', onClick: () => onOpenUpdater('update', c) },
                  { key: 'delete', icon: <DeleteOutlined />, label: 'מחיקה', danger: true, onClick: () => onDeleteConfig(c) },
                ]}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getCardcomConfigs: actionsCreators.getCardcomConfigs,
      getSchoolerCourses: actionsCreators.getSchoolerCourses,
      deleteCardcomConfig: actionsCreators.deleteCardcomConfig,
      showAlert: actionsCreators.showAlert,
      withConfirmAlert: actionsCreators.withConfirmAlert,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(CardcomConfigManager);
